import { ComputerOutlined, LocationOnOutlined } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';

import { Tag } from '../../../components/general/Tag.tsx';
import { LeistungIndexTags } from '../../../components/general/ThemaHeader/LeistungIndexTags.tsx';
import { CheckDurchgangAvailabilityOutput, ExperteFromResourcesRouter, ModulFromThemaRouter } from '../../../dtos';
import { formatTimeRangeForDate, getExperteName } from '../../../utils';
import { getLeistungstemplatesFromModul } from '../../../utils/getLeistungstemplatesFromModul.ts';
import { printModulFormat } from '../../../utils/printModulFormat.ts';
import { content as durchgangContent } from './CreateDurchgangResult.content.ts';
import { content } from './CreateDurchgangResultModul.content.ts';

type CreateDurchgangResultModulProps = {
  readonly modul: ModulFromThemaRouter;
  readonly modulResult: CheckDurchgangAvailabilityOutput['module'][number];
  readonly hasErrors: (modulResult: CheckDurchgangAvailabilityOutput['module'][number]) => boolean;
  readonly onRescheduleManually: (modulResult: CheckDurchgangAvailabilityOutput['module'][number]) => void;
  readonly expertePool:
    Pick<ExperteFromResourcesRouter, 'experteSapId' | 'vorname' | 'nachname' | 'istGesperrt'>[] | undefined;
};

export const CreateDurchgangResultModul = ({
  modul,
  modulResult,
  hasErrors,
  onRescheduleManually,
  expertePool,
}: CreateDurchgangResultModulProps) => {
  const getExperteFullName = (experteSapId: number) => {
    const experte = expertePool?.find((exp) => exp.experteSapId === experteSapId);
    if (experte) {
      return getExperteName(experte);
    }
    return experteSapId;
  };

  return (
    <Box
      sx={{
        p: 2,
        background: (theme) => theme.palette.surface.background,
        border: (theme) => `1px solid ${theme.palette.border.default}`,
        borderRadius: '4px',
        borderColor: (theme) => (hasErrors(modulResult) ? theme.palette.error.main : undefined),
      }}
      key={modul.modulnummer}
    >
      <Box display="grid" gap={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="paragraph" fontWeight={500}>
            Modul {modul.modulnummer}: {modul.titel}
          </Typography>
          <Button
            size="small"
            onClick={() => onRescheduleManually(modulResult)}
            variant="contained"
            color="secondary"
            sx={{ height: '34px' }}
          >
            {durchgangContent.rescheduleManually}
          </Button>
        </Box>
        <div className="flex gap-8">
          <Tag style="brand" size="small">
            {modul.isOnline ? (
              <ComputerOutlined sx={{ fontSize: '16px' }} />
            ) : (
              <LocationOnOutlined sx={{ fontSize: '16px' }} />
            )}
            {printModulFormat(modul.modulFormat)}
          </Tag>
          <LeistungIndexTags leistungstemplates={getLeistungstemplatesFromModul(modul)} />
        </div>

        <Box display="grid" gap={4} gridTemplateColumns="repeat(3, 1fr)" overflow="hidden">
          <Box display="flex" gap={1}>
            <Typography variant="labelMultilineLight" color="textTertiary">
              Datum
            </Typography>
            <Box>
              {modulResult.tage.map((tag, i) => (
                <Typography
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={`moduleDate-${modul.modulnummer}-${i}-${tag.zeitraum.start.toISOString()}`}
                  variant="labelMultilineLight"
                  component="p"
                >
                  {formatTimeRangeForDate(tag.zeitraum.start, tag.zeitraum.end)}
                </Typography>
              ))}
            </Box>
          </Box>

          <Box display="flex" gap={1}>
            <Typography variant="labelMultilineLight" color="textTertiary">
              Trainer:in
            </Typography>
            <Box>
              {modulResult.tage.flatMap((tag) => (
                <Typography variant="labelMultilineLight" component="p" key={`experteTag${tag.zeitraum.start}`}>
                  {tag.expertenSapIds.map((experteSapId) => getExperteFullName(experteSapId)).join(', ') || '-'}
                </Typography>
              ))}
            </Box>
          </Box>

          {!modul.isOnline && (
            <Box display="flex" gap={1}>
              <Typography variant="labelMultilineLight" color="textTertiary">
                Seminarraum
              </Typography>
              <Box display="grid">
                {modulResult.tage.map((tag, tagIndex) => (
                  <Seminarraum
                    key={`seminarraumTag${tag.zeitraum.start}`}
                    modulResult={modulResult}
                    tagIndex={tagIndex}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

function getGruppenraeumeText(tage: { gruppenraeume?: { raumSapId: number }[] }[], tagIndex: number) {
  const uniqueRaeume = new Set(tage[tagIndex].gruppenraeume?.map((gr) => gr.raumSapId).filter(Boolean));

  if (uniqueRaeume.size === 0) {
    return '';
  }
  if (uniqueRaeume.size === 1) {
    return content.gruppenraumSingular;
  }
  return content.gruppenraumPlural(uniqueRaeume.size);
}

const Seminarraum = ({
  modulResult,
  tagIndex,
}: Pick<CreateDurchgangResultModulProps, 'modulResult'> & { tagIndex: number }) => {
  const text = (modulResult.seminarraum?.kuerzel ?? '-') + getGruppenraeumeText(modulResult.tage, tagIndex);

  return (
    <Typography variant="labelMultilineLight" component="p" title={text} noWrap>
      {text}
    </Typography>
  );
};
